import {inject} from '@angular/core';
import {RedirectFunction, Route, Router} from '@angular/router';
import {
  featureEnabledGuard,
  previewFeaturesGuard,
  previewListingGuard,
} from './helpers/guards/feature-enabled.guard';
import {authGuard} from './helpers/guards/auth.guard';
import {guestGuard} from './helpers/guards/guest.guard';
import {locationContextGuard} from './helpers/guards/location-context.guard';
import {OauthCallbackComponent} from './routes/oauth-callback/oauth-callback.component';
import {MainComponent} from './routes/main.component';
import {RegisterComponent} from './routes/register/register.component';
import {LoginComponent} from './routes/login/login.component';
import {ComingSoon} from './routes/coming-soon/coming-soon';
import {LocationsDataService} from '@on-the-go-web/library/helpers';
import {ENABLE_SUBSCRIPTIONS} from './tokens';

// currentLocationId isn't restored from storage on its own (it's only set
// live by the location-context guard), so a fresh navigation needs the
// persisted recentLocationIds fallback too — same chain sidenav/bottom-nav
// use. The router invokes RedirectFunctions in an injection context per
// navigation, so inject() here (rather than at route-config build time) is fine.
function currentOrRecentLocationId(): string | undefined {
  const locationsData = inject(LocationsDataService);
  return locationsData.currentLocationId() ?? locationsData.recentLocationIds()?.[0] ?? undefined;
}

// l/current/** lets nav items link into the current location without
// resolving its id themselves, e.g. l/current/publish/42 -> /l/<id>/publish/42.
// Matched as a wildcard (rather than l/current/:section) so this also
// forwards deeper sub-paths like inbox/:id or publish/:id —
// a route matched by segment count alone drops everything past :section.
//
// Returns a UrlTree rather than a string: an absolute string redirect keeps
// only its own query params, which silently dropped e.g. the AI co-pilot's
// `inbox?getReviews=1` (opens the "Get more reviews" dialog).
export const redirectToCurrentSection: RedirectFunction = ({url, queryParams, fragment}) => {
  const locationId = currentOrRecentLocationId();
  const router = inject(Router);
  if (!locationId) return router.createUrlTree(['/'], {queryParams});
  const suffix = url.slice(2).map((segment) => segment.path);
  return router.createUrlTree(['/l', locationId, ...suffix], {
    queryParams,
    fragment: fragment ?? undefined,
  });
};

// Fallback for genuinely unmatched paths: land on the current location
// rather than trying to preserve a path that didn't match any route.
const redirectToCurrent: RedirectFunction = () => {
  const locationId = currentOrRecentLocationId();
  return locationId ? `/l/${locationId}` : '/';
};

export const appRoutes: Route[] = [
  {
    path: '',
    component: MainComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./routes/locations/locations.routes').then((r) => r.locationsRoutes),
        canActivate: [authGuard],
        data: {copilot: 'locations'},
      },
      {
        path: 'login',
        component: LoginComponent,
        canActivate: [guestGuard],
      },
      {
        path: 'location/:id',
        loadComponent: () =>
          import('./routes/locations/location/location.component').then((m) => m.LocationComponent),
        data: {isPublic: true},
      },
      {
        path: 'search/location/:id',
        loadComponent: () =>
          import('./routes/locations/location/location.component').then((m) => m.LocationComponent),
        canActivate: [authGuard],
      },
      {
        path: 'l/current/**',
        redirectTo: redirectToCurrentSection,
      },
      // Every page here operates on a single, currently-selected location —
      // the guard resolves/validates :locationId and records it as current.
      // Inbox, Publish, connect and the Google/Facebook/Instagram listings are
      // preview features (ENABLE_PREVIEW_FEATURES); plans need subscriptions.
      {
        path: 'l/:locationId',
        canActivate: [authGuard, locationContextGuard],
        children: [
          {
            path: '',
            redirectTo: 'data',
            pathMatch: 'full',
          },
          {
            path: 'analytics',
            data: {copilot: 'analytics'},
            loadComponent: () =>
              import('./routes/analytics/analytics.component').then((m) => m.AnalyticsComponent),
          },
          {
            path: 'publish',
            canActivate: [previewFeaturesGuard],
            data: {copilot: 'publish'},
            loadComponent: () =>
              import('./routes/publish/publish.component').then((m) => m.PublishComponent),
          },
          {
            path: 'publish/create-post',
            canActivate: [previewFeaturesGuard],
            data: {copilot: 'publish'},
            loadComponent: () =>
              import('./routes/publish/create-post/create-post').then((m) => m.CreatePost),
          },
          {
            // Edits a scheduled (or failed) post in Compose.
            path: 'publish/:id/edit',
            canActivate: [previewFeaturesGuard],
            data: {copilot: 'publish', copilotEntityParam: 'id'},
            loadComponent: () =>
              import('./routes/publish/create-post/create-post').then((m) => m.CreatePost),
          },
          {
            path: 'publish/:id',
            canActivate: [previewFeaturesGuard],
            data: {copilot: 'publish', copilotEntityParam: 'id'},
            loadComponent: () =>
              import('./routes/publish/post-detail/post-detail').then((m) => m.PostDetail),
          },
          {
            path: 'data',
            data: {copilot: 'data'},
            loadComponent: () =>
              import('./routes/locations/location/location.component').then(
                (m) => m.LocationComponent,
              ),
          },
          {
            path: 'inbox',
            canActivate: [previewFeaturesGuard],
            data: {copilot: 'inbox'},
            loadComponent: () =>
              import('./routes/inbox/inbox.component').then((m) => m.InboxComponent),
          },
          {
            path: 'inbox/:id',
            canActivate: [previewFeaturesGuard],
            data: {copilot: 'inbox', copilotEntityParam: 'id'},
            loadComponent: () =>
              import('./routes/inbox/inbox-detail/inbox-detail').then((m) => m.InboxDetail),
          },
          {
            path: 'listing/:listingId',
            canActivate: [previewListingGuard],
            data: {copilot: 'data'},
            loadComponent: () =>
              import('./routes/listing-detail/listing-detail').then((m) => m.ListingDetail),
          },
          {
            path: 'plans',
            canActivate: [featureEnabledGuard(ENABLE_SUBSCRIPTIONS)],
            loadComponent: () => import('./routes/plans/plans.component').then((m) => m.Plans),
          },
          {
            path: 'plans/cancel',
            canActivate: [featureEnabledGuard(ENABLE_SUBSCRIPTIONS)],
            loadComponent: () =>
              import('./routes/plans/cancel-subscription/cancel-subscription').then(
                (m) => m.CancelSubscription,
              ),
          },
          {
            path: 'discovery',
            canActivate: [featureEnabledGuard(ENABLE_SUBSCRIPTIONS)],
            loadComponent: () => import('./routes/discovery/discovery').then((m) => m.Discovery),
          },
          {
            path: 'connect',
            canActivate: [previewFeaturesGuard],
            loadComponent: () =>
              import('./routes/connect/connect-location').then((m) => m.ConnectLocation),
          },
        ],
      },
      {
        path: 'memories/:id',
        loadComponent: () => import('./routes/memories/memories').then((m) => m.Memories),
        canActivate: [authGuard],
      },
      {
        path: 'settings',
        data: {copilot: 'settings'},
        loadChildren: () =>
          import('./routes/settings/settings.routes').then((r) => r.settingsRoutes),
        canActivate: [authGuard],
      },
      {
        path: 'coming-soon',
        component: ComingSoon,
      },
    ],
  },
  {
    path: 'register',
    component: RegisterComponent,
    canActivate: [guestGuard],
  },
  {
    path: 'oauth-callback',
    component: OauthCallbackComponent,
  },
  // The unsubscribe link in trial reminder emails; works logged out.
  {
    path: 'email/unsubscribe',
    loadComponent: () =>
      import('./routes/email-unsubscribe/email-unsubscribe').then((m) => m.EmailUnsubscribe),
  },
  {
    path: 'add-business',
    loadComponent: () => import('./routes/add-business/add-business').then((m) => m.AddBusiness),
  },
  {
    path: 'claim/:id',
    loadComponent: () => import('./routes/add-business/add-business').then((m) => m.AddBusiness),
  },
  // Multi-location Analytics/Inbox, reached from the bulk-select action bar on
  // the locations list. Top-level siblings (not nested under 'locations',
  // which is already claimed by the shell's lazy-loaded locations.routes) so
  // they render without the sidenav, like add-business/register above.
  {
    path: 'locations-analytics',
    loadComponent: () =>
      import('./routes/locations/locations-analytics/locations-analytics').then(
        (m) => m.LocationsAnalytics,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'locations-inbox',
    loadComponent: () =>
      import('./routes/locations/locations-inbox/locations-inbox').then((m) => m.LocationsInbox),
    canActivate: [authGuard, previewFeaturesGuard],
  },
  // Bulk "create a post" entry point from the same bulk-select action bar.
  // Reuses CreatePost itself (it already branches on whether a `:locationId`
  // route param is present vs. a bulk `ids` query param — see its
  // constructor/submitPost) rather than duplicating the page, the same way
  // LocationComponent above is reused across two routes.
  {
    path: 'create-post',
    loadComponent: () =>
      import('./routes/publish/create-post/create-post').then((m) => m.CreatePost),
    canActivate: [authGuard, previewFeaturesGuard],
  },
  {
    path: '**',
    redirectTo: redirectToCurrent,
  },
];
